/**
 * 批量任务详情页面
 * 展示批量任务的所有子任务结果
 * 支持原图/生成图对比、批量下载、失败重试、分页浏览
 */

import type { ReactElement } from 'react'
import { useState, useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  Download,
  Archive,
  RotateCcw,
  Zap,
  Images,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  MousePointerClick,
  X,
  Clock,
  ChevronLeft,
  ChevronRight,
  Play,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useBatchTaskResults } from '@/hooks/useBatchTaskResults'
import { GroupedTaskResultGrid, TaskResultGrid, SelectAllFailedButton, BatchDownloadDialog, DOWNLOAD_MODE, ImagePackDialog } from '@/components/workflow'
import type { DownloadMode } from '@/components/workflow'
import { retryFailedTasks, cancelBatchTask, resumeBatchTask } from '@/services/taskService'
import type {
  BatchTaskGroupedResultGroup,
  BatchTaskGroupedResultOutput,
  BatchTaskInfo,
  BatchTaskResultItem,
} from '@/types/task'
import { TASK_RESULT_STATUS, BATCH_TASK_STATUS } from '@/types/task'

function formatBatchVolumeSummary(batchInfo: BatchTaskInfo): string {
  const repeatCount = batchInfo.repeat_count ?? 1
  const promptCount = batchInfo.prompt_count ?? 1
  const sourceCount = batchInfo.source_count ?? (batchInfo.generation_source_type === 'text_to_image'
    ? promptCount
    : (batchInfo.image_count ?? batchInfo.total_count))

  if (batchInfo.generation_source_type === 'text_to_image') {
    if (repeatCount > 1) {
      return `${String(sourceCount)} 条提示词 × 每条 ${String(repeatCount)} 张 = ${String(batchInfo.total_count)} 张`
    }
    return `${String(sourceCount)} 条提示词，共 ${String(batchInfo.total_count)} 张`
  }

  const imageCount = batchInfo.image_count ?? sourceCount
  if (promptCount > 1 && repeatCount > 1) {
    return `${String(imageCount)} 张原图 × ${String(promptCount)} 组提示词 × 每组 ${String(repeatCount)} 张 = ${String(batchInfo.total_count)} 张`
  }
  if (promptCount > 1) {
    return `${String(imageCount)} 张原图 × ${String(promptCount)} 组提示词 = ${String(batchInfo.total_count)} 张`
  }
  if (repeatCount > 1) {
    return `${String(sourceCount)} 张原图 × 每张 ${String(repeatCount)} 张 = ${String(batchInfo.total_count)} 张`
  }
  return `${String(batchInfo.total_count)} 张图片`
}

function buildSourceLabel(
  batchInfo: BatchTaskInfo,
  result: BatchTaskResultItem,
  groupIndex: number
): string {
  if (batchInfo.generation_source_type === 'text_to_image') {
    const prompt = result.prompt?.trim()
    if (prompt !== undefined && prompt !== '') {
      return prompt
    }
    const promptIndex = result.prompt_index ?? groupIndex
    return `提示词 #${String(promptIndex + 1)}`
  }

  const originalFilename = result.original_filename?.trim() ?? ''
  if (originalFilename !== '') {
    const extIndex = originalFilename.lastIndexOf('.')
    if (extIndex > 0) {
      return originalFilename.slice(0, extIndex)
    }
    return originalFilename
  }

  return `图片 #${String(groupIndex + 1)}`
}

function resolveDisplayGroupIndex(
  batchInfo: BatchTaskInfo,
  result: BatchTaskResultItem,
  fallbackIndex: number
): number {
  const explicitGroupOutputIndex = result.group_output_index ?? 0
  if (explicitGroupOutputIndex > 0) {
    return result.group_index ?? fallbackIndex
  }

  if (batchInfo.generation_source_type === 'text_to_image') {
    return result.prompt_index ?? result.group_index ?? result.index
  }

  return result.image_index ?? result.group_index ?? result.index
}

function buildDisplayGroupedResults(
  batchInfo: BatchTaskInfo | null,
  results: BatchTaskResultItem[]
): BatchTaskGroupedResultGroup[] {
  if (batchInfo === null || results.length === 0) {
    return []
  }

  const groupMap = new Map<number, BatchTaskGroupedResultGroup>()

  results.forEach((result, fallbackIndex) => {
    const groupIndex = resolveDisplayGroupIndex(batchInfo, result, fallbackIndex)
    const existingGroup = groupMap.get(groupIndex)

    if (existingGroup === undefined) {
      const nextGroup: BatchTaskGroupedResultGroup = {
        group_index: groupIndex,
        generation_source_type: batchInfo.generation_source_type,
        source_label: buildSourceLabel(batchInfo, result, groupIndex),
        input_url: result.input_url,
        total_outputs: 0,
        completed_outputs: 0,
        outputs: [],
      }
      if (batchInfo.generation_source_type === 'text_to_image' && result.prompt !== undefined) {
        nextGroup.prompt = result.prompt
      }
      if (result.original_filename !== undefined) {
        nextGroup.original_filename = result.original_filename
      }

      groupMap.set(groupIndex, {
        ...nextGroup,
      })
    }

    const group = groupMap.get(groupIndex)
    if (group === undefined) {
      return
    }

    const normalizedOutputIndex = (result.group_output_index ?? 0) > 0
      ? (result.group_output_index ?? 1)
      : group.outputs.length + 1

    const output: BatchTaskGroupedResultOutput = {
      task_id: result.task_id,
      status: result.status,
      output_url: result.output_url,
      error: result.error ?? null,
      process_time_ms: result.process_time_ms ?? null,
      prompt_index: result.prompt_index ?? 0,
      repeat_index: result.repeat_index ?? 0,
      group_output_index: normalizedOutputIndex,
    }
    if (result.prompt !== undefined) {
      output.prompt = result.prompt
    }

    group.outputs.push(output)
    group.total_outputs += 1
    if (result.status === TASK_RESULT_STATUS.COMPLETED && result.output_url !== null) {
      group.completed_outputs += 1
    }
  })

  return [...groupMap.values()]
    .sort((left, right) => left.group_index - right.group_index)
    .map((group) => ({
      ...group,
      outputs: [...group.outputs].sort((left, right) => {
        if (left.group_output_index !== right.group_output_index) {
          return left.group_output_index - right.group_output_index
        }
        return left.task_id.localeCompare(right.task_id)
      }),
    }))
}

/**
 * 加载骨架屏组件
 */
function LoadingSkeleton(): ReactElement {
  return (
    <div className="space-y-6">
      {/* 头部骨架 */}
      <div className="animate-pulse space-y-2">
        <div className="h-8 w-64 rounded bg-muted" />
        <div className="h-5 w-48 rounded bg-muted" />
      </div>

      {/* 操作栏骨架 */}
      <div className="flex gap-2">
        <div className="h-9 w-24 rounded bg-muted" />
        <div className="h-9 w-24 rounded bg-muted" />
        <div className="h-9 w-24 rounded bg-muted" />
      </div>

      {/* 网格骨架 */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div key={i} className="animate-pulse rounded-xl border bg-card p-3">
            <div className="mb-2 flex justify-between">
              <div className="h-4 w-8 rounded bg-muted" />
              <div className="h-4 w-12 rounded bg-muted" />
            </div>
            <div className="flex items-center gap-2">
              <div className="aspect-square flex-1 rounded-lg bg-muted" />
              <div className="h-4 w-4 rounded bg-muted" />
              <div className="aspect-square flex-1 rounded-lg bg-muted" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * 批量任务详情页面组件
 * @returns 页面 ReactElement
 */
export function BatchTaskDetailPage(): ReactElement {
  const { batchId } = useParams<{ batchId: string }>()

  // 获取批量任务详情（分页模式，100条/页）
  const {
    batchInfo,
    results,
    groupedResults,
    isLoading,
    error,
    total,
    page,
    pageSize,
    totalPages,
    setPage,
    refresh,
  } = useBatchTaskResults(batchId, 100, false)

  // 选中状态
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  // 选择模式状态（用于选择下载图片）
  const [selectionMode, setSelectionMode] = useState(false)

  // 重试状态
  const [isRetrying, setIsRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)

  // 取消任务状态
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  // 暂停/恢复状态
  const [isResuming, setIsResuming] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // 下载弹窗状态
  const [showDownloadDialog, setShowDownloadDialog] = useState(false)
  const [downloadMode, setDownloadMode] = useState<DownloadMode>(DOWNLOAD_MODE.IMAGES)
  const [downloadSelectedIds, setDownloadSelectedIds] = useState<string[] | undefined>(undefined)

  // ZIP打包弹窗状态
  const [showPackDialog, setShowPackDialog] = useState(false)

  // 确认弹窗状态
  const [showRetryConfirm, setShowRetryConfirm] = useState(false)
  const [pendingRetryIds, setPendingRetryIds] = useState<string[]>([])
  const displayGroupedResults = useMemo(
    () => buildDisplayGroupedResults(batchInfo, results),
    [batchInfo, results]
  )
  const shouldUseGroupedResults = displayGroupedResults.length > 0 || groupedResults.items.length > 0

  /**
   * 获取所有失败任务的 ID 列表
   */
  const failedIds = useMemo((): string[] => {
    return results
      .filter((r) => r.status === TASK_RESULT_STATUS.FAILED)
      .map((r) => r.task_id)
  }, [results])

  /**
   * 处理重试确认
   */
  const handleRetryConfirm = useCallback(async (): Promise<void> => {
    if (batchId === undefined || pendingRetryIds.length === 0) {
      return
    }

    setIsRetrying(true)
    setRetryError(null)
    setShowRetryConfirm(false)

    try {
      await retryFailedTasks(batchId, { task_ids: pendingRetryIds })
      // 刷新数据
      refresh()
      // 清空选中
      setSelectedIds([])
      setPendingRetryIds([])
    } catch (err) {
      const message = err instanceof Error ? err.message : '重试失败，请稍后再试'
      setRetryError(message)
    } finally {
      setIsRetrying(false)
    }
  }, [batchId, pendingRetryIds, refresh])

  /**
   * 处理重试选中
   */
  const handleRetrySelected = useCallback((): void => {
    if (selectedIds.length === 0) {
      return
    }
    setPendingRetryIds(selectedIds)
    setShowRetryConfirm(true)
  }, [selectedIds])

  /**
   * 处理单个任务重试
   */
  const handleRetrySingle = useCallback((taskId: string): void => {
    setPendingRetryIds([taskId])
    setShowRetryConfirm(true)
  }, [])

  /**
   * 处理重试所有失败任务
   */
  const handleRetryAllFailed = useCallback((): void => {
    if (failedIds.length === 0) {
      return
    }
    setPendingRetryIds(failedIds)
    setShowRetryConfirm(true)
  }, [failedIds])

  /**
   * 计算待处理数量（fallback 逻辑）
   * 如果后端未返回 queued_count，则通过公式推算
   */
  const pendingCount = useMemo((): number => {
    if (batchInfo === null) return 0
    // 如果有 queued_count，直接返回
    if (batchInfo.queued_count !== undefined) return batchInfo.queued_count
    // 否则通过公式计算：待处理数 = total - completed - failed - running
    const runningCount = batchInfo.running_count ?? 0
    return Math.max(0, batchInfo.total_count - batchInfo.completed_count - batchInfo.failed_count - runningCount)
  }, [batchInfo])

  /**
   * 判断是否有 queued_count 字段
   * 用于决定显示"排队中"还是"待处理"
   */
  const hasQueuedCount = batchInfo?.queued_count !== undefined

  /**
   * 判断是否有 running_count 字段
   * 用于决定是否显示处理中统计
   */
  const hasRunningCount = batchInfo?.running_count !== undefined

  /**
   * 计算取消任务信息
   */
  const calculateCancelInfo = useCallback((): {
    queuedCount: number
    runningCount: number | undefined
    hasRunningCount: boolean
  } => {
    if (batchInfo === null) {
      return { queuedCount: 0, runningCount: undefined, hasRunningCount: false }
    }
    // 如果有 queued_count，使用它；否则使用待处理数作为可取消数量
    const queuedCount = batchInfo.queued_count ?? pendingCount
    // running_count 可能未定义
    const runningCount = batchInfo.running_count
    return { queuedCount, runningCount, hasRunningCount: runningCount !== undefined }
  }, [batchInfo, pendingCount])

  /**
   * 处理取消任务确认
   */
  const handleCancelConfirm = useCallback(async (): Promise<void> => {
    if (batchId === undefined) {
      return
    }
    setIsCancelling(true)
    setCancelError(null)
    try {
      await cancelBatchTask(batchId)
      setShowCancelDialog(false)
      refresh()
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : '取消失败，请稍后再试')
    } finally {
      setIsCancelling(false)
    }
  }, [batchId, refresh])
  /**
   * 处理恢复任务
   */
  const handleResumeTask = useCallback(async (): Promise<void> => {
    if (batchId === undefined) {
      return
    }
    setIsResuming(true)
    setActionError(null)
    try {
      await resumeBatchTask(batchId)
      refresh()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '恢复失败，请稍后再试')
    } finally {
      setIsResuming(false)
    }
  }, [batchId, refresh])

  /**
   * 处理下载选中的图片
   * 如果未进入选择模式，则进入选择模式
   * 如果已进入选择模式且有选中图片，则开始下载
   */
  const handleDownloadSelected = useCallback((): void => {
    if (!selectionMode) {
      // 进入选择模式
      setSelectionMode(true)
      return
    }
    // 已在选择模式，开始下载
    if (selectedIds.length === 0) {
      return
    }
    setDownloadMode(DOWNLOAD_MODE.IMAGES)
    setDownloadSelectedIds(selectedIds)
    setShowDownloadDialog(true)
  }, [selectionMode, selectedIds])

  /**
   * 取消选择模式
   */
  const handleCancelSelection = useCallback((): void => {
    setSelectionMode(false)
    setSelectedIds([])
  }, [])

  /**
   * 处理导出地址
   */
  const handleExportUrls = useCallback((): void => {
    setDownloadMode(DOWNLOAD_MODE.URLS)
    setDownloadSelectedIds(selectedIds.length > 0 ? selectedIds : undefined)
    setShowDownloadDialog(true)
  }, [selectedIds])

  /**
   * 处理打包ZIP下载
   */
  const handlePackZip = useCallback((): void => {
    setShowPackDialog(true)
  }, [])

  /**
   * 获取选中任务中已完成的数量
   */
  const selectedCompletedCount = useMemo((): number => {
    return results.filter(
      (r) => selectedIds.includes(r.task_id) && r.status === TASK_RESULT_STATUS.COMPLETED
    ).length
  }, [results, selectedIds])

  // 加载中
  if (isLoading && batchInfo === null) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link to="/records/generations" aria-label="返回生图记录">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        </div>
        <LoadingSkeleton />
      </div>
    )
  }

  // 错误或不存在
  if (error !== null || batchInfo === null) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link to="/records/generations" aria-label="返回生图记录">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">任务不存在</h1>
        </div>

        <div className="rounded-2xl border bg-card p-10 text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {error?.message ?? '找不到该任务，可能已被删除或链接无效。'}
          </p>
          <div className="mt-4">
            <Button asChild>
              <Link to="/records/generations">返回生图记录</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面头部 */}
      <div className="flex items-start gap-4">
        <Button asChild variant="ghost" size="icon" className="shrink-0">
          <Link to="/records/generations" aria-label="返回生图记录">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-2xl font-bold tracking-tight">
              {batchInfo.workflow_name}
            </h1>
            {batchInfo.remark !== '' && (
              <span className="text-lg text-muted-foreground">
                - {batchInfo.remark}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Zap className="h-4 w-4" />
              消耗 {batchInfo.consumed_power} 算力
            </span>
            <span className="inline-flex items-center gap-1">
              <Images className="h-4 w-4" />
              {formatBatchVolumeSummary(batchInfo)}
            </span>
            {pendingCount > 0 && (
              <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-400">
                <Clock className="h-4 w-4" />
                {pendingCount} {hasQueuedCount ? '排队中' : '待处理'}
              </span>
            )}
            {hasRunningCount && batchInfo.running_count !== undefined && batchInfo.running_count > 0 && (
              <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                {batchInfo.running_count} 处理中
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <CheckCircle className="h-4 w-4" />
              {batchInfo.completed_count} 成功
            </span>
            {batchInfo.failed_count > 0 && (
              <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                <XCircle className="h-4 w-4" />
                {batchInfo.failed_count} 失败
              </span>
            )}
            {batchInfo.cancelled_count !== undefined && batchInfo.cancelled_count > 0 && (
              <span className="inline-flex items-center gap-1 text-slate-500 dark:text-slate-400">
                <XCircle className="h-4 w-4" />
                {batchInfo.cancelled_count} 已取消
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 操作栏 */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-card p-3">
        {/* 全选失败按钮 */}
        <SelectAllFailedButton
          failedIds={failedIds}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />

        {/* 重试选中按钮 */}
        {selectedIds.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleRetrySelected}
            disabled={isRetrying}
          >
            {isRetrying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                重试中...
              </>
            ) : (
              <>
                <RotateCcw className="h-4 w-4" />
                重试选中 ({selectedIds.length})
              </>
            )}
          </Button>
        )}

        {/* 重试所有失败按钮 */}
        {failedIds.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleRetryAllFailed}
            disabled={isRetrying}
          >
            <RotateCcw className="h-4 w-4" />
            重试所有失败 ({failedIds.length})
          </Button>
        )}

        <div className="flex-1" />



        {/* 恢复任务按钮 */}
        {batchInfo.status === BATCH_TASK_STATUS.PAUSED && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={(): void => {
              void handleResumeTask()
            }}
            disabled={isResuming || isCancelling}
          >
            {isResuming ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                恢复中...
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                恢复任务
              </>
            )}
          </Button>
        )}

        {/* 取消任务按钮（仅等待中、处理中、已暂停时显示） */}
        {(batchInfo.status === BATCH_TASK_STATUS.PENDING || batchInfo.status === BATCH_TASK_STATUS.RUNNING || batchInfo.status === BATCH_TASK_STATUS.PAUSED) && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
            onClick={(): void => {
              setShowCancelDialog(true)
            }}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                取消中...
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" />
                取消任务
              </>
            )}
          </Button>
        )}

        <Button
          asChild
          variant="outline"
          size="sm"
          className="gap-1.5"
        >
          <Link to={batchId !== undefined ? `/records/downloads?ref_type=batch_task&ref_id=${batchId}` : '/records/downloads'}>
            <Archive className="h-4 w-4" />
            下载中心
          </Link>
        </Button>

        {/* 导出Excel按钮 */}
        {batchInfo.completed_count > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleExportUrls}
          >
            <FileText className="h-4 w-4" />
            导出图片地址
          </Button>
        )}

        {/* 打包ZIP下载按钮 */}
        {batchInfo.completed_count > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handlePackZip}
          >
            <Download className="h-4 w-4" />
            打包ZIP下载
          </Button>
        )}

        {/* 选择图片下载按钮（有完成任务时显示） */}
        {batchInfo.completed_count > 0 && (
          <>
            {selectionMode && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                onClick={handleCancelSelection}
              >
                <X className="h-4 w-4" />
                取消选择
              </Button>
            )}
            <Button
              variant={selectionMode && selectedCompletedCount > 0 ? 'default' : 'outline'}
              size="sm"
              className="gap-1.5"
              onClick={handleDownloadSelected}
              disabled={selectionMode && selectedCompletedCount === 0}
            >
              {selectionMode ? (
                <>
                  <Download className="h-4 w-4" />
                  {selectedCompletedCount > 0 ? `下载选中 (${String(selectedCompletedCount)})` : '请选择图片'}
                </>
              ) : (
                <>
                  <MousePointerClick className="h-4 w-4" />
                  选择图片下载
                </>
              )}
            </Button>
          </>
        )}
      </div>

      {/* 操作错误提示 */}
      {actionError !== null && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="inline h-4 w-4 mr-2" />
          {actionError}
        </div>
      )}

      {/* 取消错误提示 */}
      {cancelError !== null && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="inline h-4 w-4 mr-2" />
          {cancelError}
        </div>
      )}

      {/* 错误提示 */}
      {retryError !== null && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
          {retryError}
        </div>
      )}

      {/* 任务结果网格 */}
      {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {shouldUseGroupedResults ? (
            <GroupedTaskResultGrid
              groups={displayGroupedResults.length > 0 ? displayGroupedResults : groupedResults.items}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onRetry={handleRetrySingle}
              isRetrying={isRetrying}
              selectionMode={selectionMode}
            />
          ) : (
            <TaskResultGrid
              results={results}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onRetry={handleRetrySingle}
              isRetrying={isRetrying}
              startIndex={(page - 1) * pageSize}
              selectionMode={selectionMode}
              promptCount={batchInfo.prompt_count ?? 1}
            />
          )}

          {/* 分页信息和控件 */}
          {results.length > 0 && (
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                共 {total} 张图片，第 {page}/{totalPages} 页
              </p>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(): void => {
                      setPage(page - 1)
                    }}
                    disabled={page === 1 || isLoading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {/* 页码按钮 */}
                  {((): (number | string)[] => {
                    const pages: (number | string)[] = []
                    const maxVisible = 5

                    if (totalPages <= maxVisible) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i)
                      }
                    } else {
                      pages.push(1)

                      if (page > 3) {
                        pages.push('...')
                      }

                      const start = Math.max(2, page - 1)
                      const end = Math.min(totalPages - 1, page + 1)

                      for (let i = start; i <= end; i++) {
                        pages.push(i)
                      }

                      if (page < totalPages - 2) {
                        pages.push('...')
                      }

                      pages.push(totalPages)
                    }

                    return pages
                  })().map((pageNum, index) =>
                    typeof pageNum === 'number' ? (
                      <Button
                        key={pageNum}
                        variant={pageNum === page ? 'default' : 'outline'}
                        size="icon"
                        className="h-8 w-8"
                        onClick={(): void => {
                          setPage(pageNum)
                        }}
                        disabled={isLoading}
                      >
                        {pageNum}
                      </Button>
                    ) : (
                      <span
                        key={`ellipsis-${String(index)}`}
                        className="px-2 text-muted-foreground"
                      >
                        ...
                      </span>
                    )
                  )}

                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(): void => {
                      setPage(page + 1)
                    }}
                    disabled={page === totalPages || isLoading}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* 重试确认弹窗 */}
      <AlertDialog open={showRetryConfirm} onOpenChange={setShowRetryConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认重试</AlertDialogTitle>
            <AlertDialogDescription>
              即将重试 {pendingRetryIds.length} 张失败的图片，预计消耗{' '}
              <span className="font-medium text-foreground">
                {String(pendingRetryIds.length * (batchInfo.consumed_power / batchInfo.total_count || 1))}
              </span>{' '}
              算力。是否继续？
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={(): void => {
                void handleRetryConfirm()
              }}
            >
              确认重试
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 批量下载弹窗 */}
      {batchId !== undefined && (
        <BatchDownloadDialog
          open={showDownloadDialog}
          onOpenChange={setShowDownloadDialog}
          batchId={batchId}
          remark={batchInfo.remark}
          expectedCount={downloadSelectedIds !== undefined ? selectedCompletedCount : batchInfo.completed_count}
          mode={downloadMode}
          {...(downloadSelectedIds !== undefined ? { selectedTaskIds: downloadSelectedIds } : {})}
        />
      )}

      {/* ZIP打包弹窗 */}
      {batchId !== undefined && (
        <ImagePackDialog
          open={showPackDialog}
          onOpenChange={setShowPackDialog}
          batchId={batchId}
          remark={batchInfo.remark}
          totalCount={batchInfo.completed_count}
        />
      )}

      {/* 取消任务确认弹窗 */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认取消任务</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>确认要取消这个批量任务吗？</p>
                {((): ReactElement => {
                  const cancelInfo = calculateCancelInfo()
                  return (
                    <div className="rounded-lg border bg-muted/50 p-3 space-y-2 text-sm">
                      {cancelInfo.hasRunningCount && cancelInfo.runningCount !== undefined && cancelInfo.runningCount > 0 && (
                        <div className="flex items-start gap-2 text-blue-600 dark:text-blue-400">
                          <Loader2 className="h-4 w-4 mt-0.5 animate-spin shrink-0" />
                          <span>正在处理中: {cancelInfo.runningCount} 张（已调用生图接口，无法取消）</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {hasQueuedCount ? '可取消的排队任务:' : '可取消的待处理任务:'}
                        </span>
                        <span className="font-medium text-foreground">{cancelInfo.queuedCount} 张</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">费用说明:</span>
                        <span className="font-medium text-foreground">未完成的任务不会扣费</span>
                      </div>
                    </div>
                  )
                })()}
                <p className="text-xs text-muted-foreground">
                  {hasQueuedCount && hasRunningCount
                    ? '说明：取消操作仅会终止「排队中」的任务，未完成的任务不会扣费。「处理中」状态的任务无法取消，将继续执行完成。已完成和已失败的任务不受影响。'
                    : '说明：取消操作仅会终止「待处理」的任务，未完成的任务不会扣费。已完成和已失败的任务不受影响。'}
                </p>
                {cancelError !== null && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-2 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{cancelError}</span>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>返回</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e): void => {
                e.preventDefault()
                void handleCancelConfirm()
              }}
              disabled={isCancelling}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isCancelling ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  取消中...
                </>
              ) : (
                '确认取消任务'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
